import styles from './statistics-page.module.scss'
import cl from 'classnames'
import { observer } from 'mobx-react-lite'
import { PageContainer } from 'components'
import { useRootStore } from 'mobx-stores/store-provider'
import { useEffect, useState } from 'react'
import { IconStatistics, PageHeader, PeriodPicker } from '@touche-lts/panel-ui/dist'
import {
    Chart,
    registerables
  } from 'chart.js';
import { Chart as Customchart } from 'react-chartjs-2';
import { StatisticsTable } from 'pages'
import moment from 'moment'
import { StatisticsDatePeriod } from 'models/statistics'
import { PeriodValue } from '@touche-lts/panel-ui/dist/components/shared/date-range-picker'
import { useGetIsResponsiveScreen } from 'hooks/custom-hooks'
import { User } from 'services'
import { useParams } from 'react-router'

Chart.register(
    ...registerables
);

const StatisticsPageComponent = () => {
  const { storeId } = useParams<{ storeId?: string }>()
  const {
    userStore: { setManagementStoreId },
    statisticsStore: {
        getHighLights,
        statisticsHighlights,
        storeAdminHighlights,
        getGraphInfo,
        statisticsGraph,
        statiSticsGraphData,
        setPeriodDate,
        setCustomPeriod
    },
    statisticsTableStore: {
        triggerSearch
    }
  } = useRootStore()

  const isStoreAdmin = User.isStoreAdmin
  const highlights = isStoreAdmin ? storeAdminHighlights : statisticsHighlights

  const {isResponsive} = useGetIsResponsiveScreen('(max-width: 768px)')

  const selectDatePeriod = (ev): void => {
    const targetValue = ev.target.value;
    let period = null;
    if(targetValue === StatisticsDatePeriod.WEEK) {
        period = moment().subtract(7,'d').toDate().toISOString()
    } else if(targetValue === StatisticsDatePeriod.MONTH) {
        period = moment().subtract(1,'months').toDate().toISOString()
    } else {
        period = moment().subtract(1,'years').toDate().toISOString()
    }
    setPeriodDate(period)
    triggerSearch({resetPageNumber: true})
    setStartPeriod(null)
    setEndPeriod(null)
  }

  const [startPeriod, setStartPeriod] = useState(null);
  const [endPeriod, setEndPeriod] = useState(null);

  const setPeriod = (period: PeriodValue) => {
    setStartPeriod(period ? period.start : null)
    setEndPeriod(period ? period.end : null)
    setCustomPeriod(period)
    triggerSearch({resetPageNumber: true})
  }

  useEffect(() => {
    setManagementStoreId(storeId)
  }, [storeId, setManagementStoreId])

  useEffect(() => {
    getHighLights()
    getGraphInfo()
    triggerSearch({resetPageNumber: true})
  }, [getHighLights, getGraphInfo, triggerSearch])


  return (
    <PageContainer className={cl(styles.scrollable)}> 
        <div className='container'>
            <div className={styles.headerToolbar}>
                <PageHeader
                    navSegments={[
                        {
                            icon: IconStatistics,
                            label: 'Store statistics'
                        }
                    ]}
                    colorPreset='dark'
                />
                <div className={styles.periodSelectorWrapper}>
                    <p className={styles.selectedPeriodText}>Period</p>
                    <select id="statistic-period" onChange={ev => selectDatePeriod(ev)} className={styles.selectPeriod}>
                        <option value={StatisticsDatePeriod.WEEK}>{StatisticsDatePeriod.WEEK}</option>
                        <option value={StatisticsDatePeriod.MONTH}>{StatisticsDatePeriod.MONTH}</option>
                        <option value={StatisticsDatePeriod.YEAR}>{StatisticsDatePeriod.YEAR}</option>
                    </select>
                </div>
                <div className={styles.periodPickerWrapper}>
                    <PeriodPicker
                        period={startPeriod
                            ? { start: startPeriod, end: endPeriod }
                            : null
                        }
                        setPeriod={period => setPeriod(period ? period: null)}
                        dateFormat='DD.MM.YY'
                        noIcon
                        colorPreset='dark'
                        label=''
                        placeholder='Custom'
                        className={styles.customFilterComponent}
                    />
                </div>
            </div>
            <div className={styles.highlightBox}>
                <p>Highlights</p>
                <div className="d-flex justify-content-between flex-wrap gap-3 gap-sm-0">
                    <div className={styles.highLightStat}>
                        <h1>${highlights?.totalRevenue}</h1>
                        <span>Revenue</span>
                    </div>
                    {/* <div className={cl(styles.highLightStat, styles.borderLeftHighlights)}>
                        <h1>{statisticsHighlights?.productsSold}</h1>
                        <span>Active products</span>
                    </div> */}
                    <div className={cl(styles.highLightStat, styles.borderLeftHighlights)}>
                        <h1>{highlights?.productsSold}</h1>
                        <span>Products sold</span>
                    </div>
                    <div className={cl(styles.highLightStat, styles.borderLeftHighlights)}>
                        <h1>{highlights?.bestSellingProduct?.name}</h1>
                        <span>Best selling product</span>
                    </div>
                    <div className={cl(styles.highLightStat, styles.borderLeftHighlights)}>
                        {isStoreAdmin ? (
                            <h1>{(highlights as any)?.totalVisitors}</h1>
                        ) : (
                            <h1>{(highlights as any)?.visitingUsers}</h1>
                        )}
                        <span>Visiting users</span>
                    </div>
                    {isStoreAdmin && (
                        <div className={cl(styles.highLightStat, styles.borderLeftHighlights)}>
                            <h1>{(highlights as any)?.totalOrders}</h1>
                            <span>Total orders</span>
                        </div>
                    )}
                </div>
            </div>
            <div className={cl(styles.statChartTable, 'mt-4')}>
                <div className='d-flex justify-content-between'>
                    <h2>Overview</h2>
                    <p className={styles.totalRevenue}>Total revenue for selected period: <span className={styles.totalRevenueValue}>${
                    statisticsGraph && 
                    !!statisticsGraph.length && 
                    statisticsGraph
                    .map(statInfo => statInfo.revenueToday)
                    .reduce((previousValue, currentValue) => 
                        previousValue + currentValue
                    )}</span></p>
                </div>
                <div className={styles.periodChartContainer}>
                    <p className={styles.productsText}>Products</p>
                    {statisticsGraph && <Customchart type='bar' height={100} data={statiSticsGraphData} />}
                </div>
                <div className={styles.periodText}>
                    Period
                </div>
                <hr />
                <StatisticsTable />
            </div>
        </div>
    </PageContainer>
  )
}

const StatisticsPage = observer(StatisticsPageComponent)
export { StatisticsPage }
